import { useCallback, useState } from 'react'
import { Toast } from '@/components/Toast'
import { ID } from '@/types/common'
import classRecordApi from '../api'
import {
  BatchEditAttendanceMode,
  BatchEditTeacherValue,
} from '../page-types'
import { ClassRecordStatus } from '../types'

type UseClassRecordBatchEditOptions = {
  selectedStudentId: ID | undefined
  monthText: string
  batchSelectedIds: ID[]
  setBatchSelectedIds: (ids: ID[]) => void
  loadRecords: () => Promise<void>
}

export function useClassRecordBatchEdit({
  selectedStudentId,
  monthText,
  batchSelectedIds,
  setBatchSelectedIds,
  loadRecords,
}: UseClassRecordBatchEditOptions) {
  const [batchEditModalOpen, setBatchEditModalOpen] = useState(false)
  const [batchEditAttendanceMode, setBatchEditAttendanceMode] =
    useState<BatchEditAttendanceMode>('unchanged')
  const [batchEditTeacherValue, setBatchEditTeacherValue] =
    useState<BatchEditTeacherValue>('unchanged')
  const [batchEditNote, setBatchEditNote] = useState('')
  const [batchEditSaving, setBatchEditSaving] = useState(false)

  const handleOpenBatchEdit = useCallback(() => {
    if (!selectedStudentId || !monthText) {
      Toast.warning('请先选择学生和月份并查询')
      return
    }
    if (batchSelectedIds.length === 0) {
      Toast.warning('请先勾选要批量编辑的记录')
      return
    }
    setBatchEditAttendanceMode('unchanged')
    setBatchEditTeacherValue('unchanged')
    setBatchEditNote('')
    setBatchEditModalOpen(true)
  }, [batchSelectedIds.length, monthText, selectedStudentId])

  const handleSubmitBatchEdit = useCallback(async () => {
    if (batchSelectedIds.length === 0) {
      Toast.warning('请选择记录')
      return
    }

    const trimmedNote = batchEditNote.trim()
    if (batchEditAttendanceMode === 'unchanged' && batchEditTeacherValue === 'unchanged' && !trimmedNote) {
      Toast.warning('请至少选择一个要修改的字段')
      return
    }

    try {
      setBatchEditSaving(true)
      const payload: {
        classRecordStatus?: ClassRecordStatus
        note?: string
        teacherId?: ID
      } = {}

      if (batchEditAttendanceMode !== 'unchanged') {
        payload.classRecordStatus = batchEditAttendanceMode
      }

      if (batchEditTeacherValue !== 'unchanged') {
        payload.teacherId = batchEditTeacherValue
      }

      if (trimmedNote) {
        payload.note = trimmedNote
      }

      const res = await classRecordApi.batchUpdate({
        ids: batchSelectedIds,
        ...payload,
      })
      Toast.success(res?.message || `已批量更新 ${batchSelectedIds.length} 条记录`)
      setBatchEditModalOpen(false)
      setBatchSelectedIds([])
      await loadRecords()
    } catch (error) {
      Toast.error('批量编辑失败')
      console.error('批量编辑失败:', error)
    } finally {
      setBatchEditSaving(false)
    }
  }, [
    batchEditAttendanceMode,
    batchEditNote,
    batchEditTeacherValue,
    batchSelectedIds,
    loadRecords,
    setBatchSelectedIds,
  ])

  return {
    batchEditModalOpen,
    setBatchEditModalOpen,
    batchEditAttendanceMode,
    setBatchEditAttendanceMode,
    batchEditTeacherValue,
    setBatchEditTeacherValue,
    batchEditNote,
    setBatchEditNote,
    batchEditSaving,
    handleOpenBatchEdit,
    handleSubmitBatchEdit,
  }
}
